import type { Book, CompletedReadingSession, ReadingSession, ReadingStatistics } from '../domain/entities/types';
import { bookRepository, progressRepository, sessionRepository } from '../repositories/dexie/repositories';
import { completionMonth, completionYear, toLocalDate } from '../utils/core';
import { formatBibleChapterRange } from '../domain/bibleCanon';
import { epubEquivalentPageContributions } from './epubEquivalentPages';

export type StatisticsPeriod = 'month' | 'year' | 'total';

const dateInPeriod = (date: string | undefined, period: StatisticsPeriod, referenceDate: Date): boolean => {
  if (period === 'total') return true;
  if (!date) return false;
  const year = String(referenceDate.getFullYear());
  if (period === 'year') return date.startsWith(year);
  return date.startsWith(`${year}-${String(referenceDate.getMonth() + 1).padStart(2, '0')}`);
};

const historicalInPeriod = (book: Book, period: StatisticsPeriod, referenceDate: Date): boolean => {
  if (book.status !== 'finished' || book.completionOrigin !== 'historical' || !book.historicalCompletion) return false;
  if (period === 'total') return true;
  const year = completionYear(book.historicalCompletion);
  if (period === 'year') return year === referenceDate.getFullYear();
  const month = completionMonth(book.historicalCompletion);
  return month?.year === referenceDate.getFullYear() && month.month === referenceDate.getMonth() + 1;
};

const trackedFinishedInPeriod = (book: Book, period: StatisticsPeriod, referenceDate: Date): boolean =>
  book.status === 'finished' && book.completionOrigin !== 'historical' && dateInPeriod(book.finishedOn, period, referenceDate);

const historicalPageTotal = (book: Book): number =>
  book.completionOrigin === 'historical' && book.status === 'finished' && book.progress.kind === 'pages'
    ? book.progress.totalPages ?? 0
    : 0;
const isCompleted = (session: ReadingSession): session is CompletedReadingSession => session.status === 'completed';
const entryDelta = (entry: Awaited<ReturnType<typeof progressRepository.listAll>>[number]): number =>
  entry.kind === 'pages'
    ? entry.deltaPages
    : entry.kind === 'audio_time'
      ? entry.deltaMinutes
      : entry.kind === 'bible_chapters'
        ? entry.pagesRead
        : entry.deltaPercent;
const entryPages = (entry: Awaited<ReturnType<typeof progressRepository.listAll>>[number]): number =>
  entry.kind === 'pages' ? entry.deltaPages : entry.kind === 'bible_chapters' ? entry.pagesRead : 0;
const entryReadingDurationMs = (entry: Awaited<ReturnType<typeof progressRepository.listAll>>[number]): number =>
  entry.readingTime?.source === 'manual' || entry.readingTime?.source === 'estimated'
    ? entry.readingTime.durationMs
    : 0;

export const statisticsService = {
  async get(period: StatisticsPeriod, referenceDate = new Date()): Promise<ReadingStatistics> {
    const [books, entries, sessions] = await Promise.all([
      bookRepository.list(), progressRepository.listAll(), sessionRepository.listCompleted()
    ]);
    const epubContributions = epubEquivalentPageContributions(entries);
    const periodEpubContributions = epubContributions.filter(({ entry }) =>
      dateInPeriod(entry.occurredOn, period, referenceDate));
    const effective = entries.filter((entry) => !entry.supersededByEntryId && dateInPeriod(entry.occurredOn, period, referenceDate));
    const completed = sessions.filter(isCompleted).filter((session) => dateInPeriod(session.startedOn, period, referenceDate));
    const trackedDurations = completed.map(({ durationMs }) => durationMs).filter((durationMs) => durationMs > 0);
    const manuallyRecordedDurations = effective.flatMap((entry) => {
      const readingTime = entry.readingTime;
      return !entry.sessionId && readingTime?.source === 'manual' && readingTime.durationMs > 0
        ? [readingTime.durationMs]
        : [];
    });
    const knownReadingDurations = [...trackedDurations, ...manuallyRecordedDurations];
    const knownReadingDurationMs = knownReadingDurations.reduce((sum, durationMs) => sum + durationMs, 0);
    const trackedPages = effective.reduce((sum, entry) => sum + entryPages(entry), 0)
      + periodEpubContributions.reduce((sum, { deltaPages }) => sum + deltaPages, 0);
    const historicalBooks = books.filter((book) => historicalInPeriod(book, period, referenceDate));
    const historicalPages = historicalBooks.reduce((sum, book) => sum + historicalPageTotal(book), 0);
    const pages = trackedPages + historicalPages;
    const trackedDurationMs = completed.reduce((sum, session) => sum + session.durationMs, 0);
    const manualDurationMs = effective.reduce((sum, entry) => sum + (entry.readingTime?.source === 'manual' ? entry.readingTime.durationMs : 0), 0);
    const estimatedDurationMs = effective.reduce((sum, entry) => sum + (entry.readingTime?.source === 'estimated' ? entry.readingTime.durationMs : 0), 0);
    const activeDates = new Set<string>();
    for (const entry of effective) {
      if (entry.kind === 'epub') continue;
      const delta = entryDelta(entry);
      if (delta > 0) activeDates.add(entry.occurredOn);
    }
    for (const { entry, deltaPages } of periodEpubContributions) {
      if (deltaPages > 0) activeDates.add(entry.occurredOn);
    }
    for (const session of completed) if (session.durationMs > 0) activeDates.add(session.startedOn);
    return {
      registeredBooks: books.length,
      finishedBooks: books.filter((book) => trackedFinishedInPeriod(book, period, referenceDate) || historicalInPeriod(book, period, referenceDate)).length,
      readingBooks: books.filter(({ status }) => status === 'reading').length,
      abandonedBooks: books.filter(({ status }) => status === 'abandoned').length,
      pages,
      trackedPages,
      historicalPages,
      durationMs: trackedDurationMs + effective.reduce((sum, entry) => sum + entryReadingDurationMs(entry), 0),
      trackedDurationMs,
      manualDurationMs,
      estimatedDurationMs,
      sessions: completed.length,
      averageSessionDurationMs: knownReadingDurations.length ? knownReadingDurationMs / knownReadingDurations.length : 0,
      longestSessionDurationMs: knownReadingDurations.length ? Math.max(...knownReadingDurations) : 0,
      activeDays: activeDates.size,
      averageMonthlyPages: (() => {
        const historicalWithMonth = historicalBooks.filter((book) => completionMonth(book.historicalCompletion));
        const attributablePages = trackedPages + historicalWithMonth.reduce((sum, book) => sum + historicalPageTotal(book), 0);
        if (period === 'month') return attributablePages;
        if (period === 'year') {
          const now = new Date();
          const divisor = referenceDate.getFullYear() === now.getFullYear() ? now.getMonth() + 1 : 12;
          return attributablePages / divisor;
        }
        const months = new Set(effective
          .filter((entry) => entry.kind !== 'epub')
          .map(({ occurredOn }) => occurredOn.slice(0, 7)));
        periodEpubContributions.forEach(({ entry, deltaPages }) => {
          if (deltaPages > 0) months.add(entry.occurredOn.slice(0, 7));
        });
        historicalWithMonth.forEach((book) => {
          const value = completionMonth(book.historicalCompletion);
          if (value) months.add(`${value.year}-${String(value.month).padStart(2, '0')}`);
        });
        return attributablePages / Math.max(1, months.size);
      })(),
      averageDailyPages: activeDates.size ? trackedPages / activeDates.size : 0
    };
  },

  async getForBook(bookId: string) {
    const [entries, sessions] = await Promise.all([
      progressRepository.listByBook(bookId), sessionRepository.listByBook(bookId)
    ]);
    const epubContributions = epubEquivalentPageContributions(entries);
    const effective = entries.filter((entry) => !entry.supersededByEntryId);
    const completed = sessions.filter(isCompleted);
    const pageEntries = effective.filter((entry) => entry.kind === 'pages' || entry.kind === 'bible_chapters');
    const pageContributions = [
      ...pageEntries.map((entry) => ({ entry, deltaPages: entryPages(entry) })),
      ...epubContributions
    ];
    const completedById = new Map(completed.map((session) => [session.id, session]));
    const pages = pageContributions.reduce((sum, { deltaPages }) => sum + deltaPages, 0);
    const pagesInSessions = pageContributions.reduce((sum, { entry, deltaPages }) =>
      sum + (entry.sessionId && completedById.has(entry.sessionId) ? deltaPages : 0), 0);
    const trackedDurationMs = completed.reduce((sum, session) => sum + session.durationMs, 0);
    const manualDurationMs = effective.reduce((sum, entry) => sum + (entry.readingTime?.source === 'manual' ? entry.readingTime.durationMs : 0), 0);
    const estimatedDurationMs = effective.reduce((sum, entry) => sum + (entry.readingTime?.source === 'estimated' ? entry.readingTime.durationMs : 0), 0);
    const durationMs = trackedDurationMs + manualDurationMs + estimatedDurationMs;
    let trustedPages = 0;
    let trustedDurationMs = 0;
    for (const { entry, deltaPages: pageCount } of pageContributions) {
      if (pageCount <= 0) continue;
      if (entry.sessionId) {
        const session = completedById.get(entry.sessionId);
        if (!session || session.durationMs <= 0) continue;
        trustedPages += pageCount;
        trustedDurationMs += session.durationMs;
      } else if (entry.readingTime?.source === 'manual') {
        trustedPages += pageCount;
        trustedDurationMs += entry.readingTime.durationMs;
      }
    }
    const activeDays = new Set(effective.filter((entry) => {
      if (entry.kind === 'pages') return entry.deltaPages > 0;
      if (entry.kind === 'audio_time') return entry.deltaMinutes > 0;
      if (entry.kind === 'audio_percent') return entry.deltaPercent > 0;
      if (entry.kind === 'epub') return false;
      return entry.pagesRead > 0 || entry.chapterIds.length > 0;
    }).map(({ occurredOn }) => occurredOn));
    for (const { entry, deltaPages } of epubContributions) {
      if (deltaPages > 0) activeDays.add(entry.occurredOn);
    }
    const lastReadOn = [
      ...effective.filter((entry) => entry.kind !== 'epub' && entryDelta(entry) > 0).map(({ occurredOn }) => occurredOn),
      ...epubContributions.filter(({ deltaPages }) => deltaPages > 0).map(({ entry }) => entry.occurredOn),
      ...completed.filter(({ durationMs: sessionDurationMs }) => sessionDurationMs > 0).map(({ startedOn }) => startedOn)
    ].sort((a, b) => b.localeCompare(a))[0];
    const entriesById = new Map(entries.map((entry) => [entry.id, entry]));
    const logicalRecordedAt = (entry: typeof entries[number], visited = new Set<string>()): string => {
      if (entry.source !== 'correction' || visited.has(entry.id)) return entry.recordedAt;
      visited.add(entry.id);
      return (entry.correctionOfEntryIds ?? [])
        .flatMap((id) => entriesById.get(id) ? [logicalRecordedAt(entriesById.get(id)!, visited)] : [])
        .sort()[0] ?? entry.recordedAt;
    };
    const lastBibleEntry = effective
      .filter((entry) => entry.kind === 'bible_chapters' && !entry.isBaseline && entry.chapterIds.length > 0)
      .sort((left, right) => right.occurredOn.localeCompare(left.occurredOn)
        || logicalRecordedAt(right).localeCompare(logicalRecordedAt(left)))[0];
    return {
      sessions: completed.length,
      durationMs,
      trackedDurationMs,
      manualDurationMs,
      estimatedDurationMs,
      pages,
      activeDays: activeDays.size,
      lastReadOn,
      lastBibleRange: lastBibleEntry?.kind === 'bible_chapters' ? formatBibleChapterRange(lastBibleEntry.chapterIds) : undefined,
      pagesPerActiveDay: activeDays.size ? pages / activeDays.size : 0,
      pagesPerSession: completed.length ? pagesInSessions / completed.length : 0,
      pagesPerHour: trustedDurationMs ? trustedPages / (trustedDurationMs / 3_600_000) : 0
    };
  },

  async forecast(book: Book, referenceDate = new Date()): Promise<{ amount: number; label: string } | undefined> {
    if (book.completionOrigin === 'historical') return undefined;
    if (book.progress.kind === 'bible_chapters' || book.progress.kind === 'epub') return undefined;
    if (book.progress.kind === 'pages' && book.progress.totalPages === undefined) return undefined;
    const entries = (await progressRepository.listByBook(book.id))
      .filter((entry) => !entry.supersededByEntryId && entry.kind === book.progress.kind && entryDelta(entry) > 0)
      .sort((a, b) => b.occurredOn.localeCompare(a.occurredOn));
    const cutoff = new Date(referenceDate);
    cutoff.setDate(cutoff.getDate() - 30);
    const cutoffText = toLocalDate(cutoff);
    const recent = entries.filter((entry) => entry.occurredOn >= cutoffText);
    const dates = [...new Set(recent.map((entry) => entry.occurredOn))].slice(0, 7);
    if (dates.length < 3) return undefined;
    let delta = 0;
    for (const entry of recent) {
      if (!dates.includes(entry.occurredOn)) continue;
      delta += entryDelta(entry);
    }
    const pace = delta / dates.length;
    if (pace <= 0) return undefined;
    const remaining = book.progress.kind === 'pages'
      ? (book.progress.totalPages ?? book.progress.currentPage) - book.progress.currentPage
      : book.progress.kind === 'audio_time'
        ? book.progress.totalMinutes - book.progress.currentMinutes
        : book.progress.kind === 'audio_percent' ? 100 - book.progress.currentPercent : 0;
    const amount = Math.max(1, Math.ceil(remaining / pace));
    return { amount, label: amount === 1 ? 'dia de leitura' : 'dias de leitura' };
  },

  async pagesByMonth(year: number): Promise<number[]> {
    const [entries, books] = await Promise.all([progressRepository.listAll(), bookRepository.list()]);
    const effective = entries.filter((entry) => !entry.supersededByEntryId && (entry.kind === 'pages' || entry.kind === 'bible_chapters'));
    const epubContributions = epubEquivalentPageContributions(entries);
    const months = Array.from({ length: 12 }, () => 0);
    for (const entry of effective) {
      if (!entry.occurredOn.startsWith(String(year))) continue;
      const month = Number(entry.occurredOn.slice(5, 7)) - 1;
      if (month >= 0 && month < 12) months[month] = (months[month] ?? 0) + entryPages(entry);
    }
    for (const { entry, deltaPages } of epubContributions) {
      if (!entry.occurredOn.startsWith(String(year))) continue;
      const month = Number(entry.occurredOn.slice(5, 7)) - 1;
      if (month >= 0 && month < 12) months[month] = (months[month] ?? 0) + deltaPages;
    }
    for (const book of books) {
      const completion = completionMonth(book.historicalCompletion);
      if (book.completionOrigin !== 'historical' || book.status !== 'finished' || !completion || completion.year !== year) continue;
      const pages = historicalPageTotal(book);
      months[completion.month - 1] = (months[completion.month - 1] ?? 0) + pages;
    }
    return months;
  },

  async pagesByDay(referenceDate: Date): Promise<number[]> {
    const year = referenceDate.getFullYear();
    const month = referenceDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const monthPrefix = `${year}-${String(month + 1).padStart(2, '0')}-`;
    const days = Array.from({ length: daysInMonth }, () => 0);
    const entries = await progressRepository.listAll();

    for (const entry of entries) {
      if (entry.supersededByEntryId || (entry.kind !== 'pages' && entry.kind !== 'bible_chapters') || !entry.occurredOn.startsWith(monthPrefix)) continue;
      const day = Number(entry.occurredOn.slice(8, 10));
      if (day < 1 || day > daysInMonth) continue;
      days[day - 1] = (days[day - 1] ?? 0) + entryPages(entry);
    }
    for (const { entry, deltaPages } of epubEquivalentPageContributions(entries)) {
      if (!entry.occurredOn.startsWith(monthPrefix)) continue;
      const day = Number(entry.occurredOn.slice(8, 10));
      if (day < 1 || day > daysInMonth) continue;
      days[day - 1] = (days[day - 1] ?? 0) + deltaPages;
    }

    return days;
  },

  async durationByDay(referenceDate: Date): Promise<number[]> {
    const year = referenceDate.getFullYear();
    const month = referenceDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const monthPrefix = `${year}-${String(month + 1).padStart(2, '0')}-`;
    const days = Array.from({ length: daysInMonth }, () => 0);
    const [entries, sessions] = await Promise.all([
      progressRepository.listAll(),
      sessionRepository.listCompleted()
    ]);

    for (const entry of entries) {
      if (entry.supersededByEntryId || !entry.occurredOn.startsWith(monthPrefix)) continue;
      const day = Number(entry.occurredOn.slice(8, 10));
      if (day < 1 || day > daysInMonth) continue;
      days[day - 1] = (days[day - 1] ?? 0) + entryReadingDurationMs(entry);
    }
    for (const session of sessions) {
      if (!isCompleted(session) || !session.startedOn.startsWith(monthPrefix)) continue;
      const day = Number(session.startedOn.slice(8, 10));
      if (day < 1 || day > daysInMonth) continue;
      days[day - 1] = (days[day - 1] ?? 0) + session.durationMs;
    }

    return days;
  }
};
